import * as React from 'react';

import { useSpring, animated } from '@react-spring/web';
import cx from 'classnames';

// eslint-disable-next-line @babel/new-cap
const LogoAnimation = async (next: typeof useSpring) => {
    await next({
        delay: 200,
        opacity: 1,
        transform: 'translate3d(0,0,0)',
        from: { opacity: 0, transform: `translate3d(0,28px, 0)` },
    });
    await next({
        delay: 600,
        opacity: 0,
    });
};

type LogoProps = {
    isAnimated?: boolean;
    width?: number | string;
    color?: string;
    a11yLabel?: string;
};

function Logo(props: LogoProps) {
    const { isAnimated, color, width, a11yLabel } = props;

    const className = cx({
        logotype: true,
    });

    const logoElement = (
        <span className={className} role={a11yLabel ? 'img' : undefined} aria-label={a11yLabel}>
            <svg
                className="logotype__image"
                viewBox="0 0 117 21"
                style={{
                    width,
                    height: width ? 'auto' : undefined,
                    minWidth: width ? '120px' : undefined,
                    color,
                }}
                aria-hidden="true"
            >
                <path
                    fill="currentColor"
                    d="M14.31 3.396H7.515a1.61 1.61 0 0 1-1.61-1.612c0-.89.72-1.612 1.61-1.612h6.793c.889 0 1.61.722 1.61 1.612 0 .89-.72 1.612-1.61 1.612ZM19.231 8.769H2.593C1.713 8.769 1 8.047 1 7.157c0-.89.713-1.612 1.593-1.612H19.23c.88 0 1.593.722 1.593 1.612 0 .89-.713 1.612-1.593 1.612ZM17.826 14.14H4a1.601 1.601 0 0 1-1.591-1.611c0-.89.712-1.612 1.59-1.612h13.828c.878 0 1.59.722 1.59 1.612 0 .89-.712 1.612-1.59 1.612h-.001ZM13.553 19.513h-5.28a1.61 1.61 0 0 1-1.61-1.612c0-.89.72-1.612 1.61-1.612h5.28c.888 0 1.609.722 1.609 1.612 0 .89-.72 1.612-1.61 1.612ZM104.178 11.643c.135-2.134 1.838-3.512 4.379-3.512 2.406 0 4.108 1.378 4.108 3.35 0 .08-.053.162-.189.162h-8.298Zm4.568 8.51c2.514 0 4.703-1.027 5.973-2.81.19-.27.298-.567.298-.864 0-.675-.514-1.162-1.189-1.162-.432 0-.784.19-1.244.675-1.107 1.188-2.27 1.729-3.757 1.729-2.703 0-4.541-1.567-4.676-3.971h10.973c.298 0 .46-.162.46-.432v-.297c0-4.377-2.785-7.24-7.055-7.24s-7.325 2.89-7.325 7.24 2.973 7.132 7.541 7.132h.001Zm-10.433 0c.865 0 1.487-.649 1.487-1.513V1.513C99.8.648 99.151 0 98.313 0c-.838 0-1.487.649-1.487 1.513V18.64c0 .865.65 1.513 1.487 1.513Zm-16.568 0c.838 0 1.432-.649 1.432-1.513v-.864c.92 1.512 2.73 2.377 5 2.377 4.298 0 7.244-2.944 7.244-7.24 0-4.295-2.919-7.132-7-7.132-2.297 0-4.298.972-5.162 2.54V1.513C83.26.648 82.61 0 81.772 0c-.838 0-1.487.649-1.487 1.513V18.64c0 .865.622 1.513 1.46 1.513Zm6.027-2.431c-2.92 0-4.649-1.756-4.649-4.755 0-2.999 1.73-4.755 4.65-4.755 2.918 0 4.675 1.756 4.675 4.755 0 2.999-1.73 4.755-4.676 4.755Zm-40.408 2.431c1.973 0 3.676-.865 4.487-2.243v.73c0 .864.649 1.512 1.487 1.512.837 0 1.487-.649 1.487-1.513V7.726c0-.865-.65-1.513-1.487-1.513-.838 0-1.487.649-1.487 1.513v6.537c0 2.323-1.244 3.593-3.514 3.593-2.27 0-3.541-1.27-3.541-3.593V7.726c0-.865-.65-1.513-1.487-1.513-.838 0-1.487.649-1.487 1.513v6.699c0 3.674 1.973 5.727 5.54 5.727l.002.001ZM27.85 7.943V2.727c0-.108.108-.216.216-.216h5.784c1.865 0 2.973 1 2.973 2.702s-1.108 2.728-2.973 2.728h-6ZM25.282 19.72h8.812c3.892 0 6.325-2.215 6.325-5.727 0-2.756-1.27-4.322-3.92-4.809 2.108-.432 3.324-1.972 3.324-4.187 0-3.053-2.27-4.998-5.81-4.998h-8.731a.427.427 0 0 0-.433.432v18.856c0 .244.19.432.433.432v.001Zm2.568-2.756v-6.43h6.108c2.136 0 3.433 1.27 3.433 3.323s-1.297 3.322-3.433 3.322h-5.892a.232.232 0 0 1-.216-.215Zm30.191 3.188c.865 0 1.487-.649 1.487-1.513v-6.97c0-2.215 1.27-3.593 3.352-3.593 2.081 0 3.189 1.324 3.189 3.593v6.97c0 .865.649 1.513 1.487 1.513.838 0 1.487-.649 1.487-1.513v-6.97c0-2.215 1.27-3.593 3.351-3.593 2.082 0 3.19 1.324 3.19 3.593v6.97c0 .865.648 1.513 1.486 1.513s1.487-.649 1.487-1.513v-7.132c0-3.593-2-5.727-5.352-5.727-2.216 0-3.864.946-4.757 2.702-.891-1.81-2.406-2.702-4.621-2.702-2.027 0-3.487.756-4.298 2.242v-.297c0-.864-.65-1.512-1.487-1.512-.838 0-1.487.648-1.487 1.512V18.64c0 .865.65 1.513 1.487 1.513Z"
                />
            </svg>
        </span>
    );

    // @TODO: find way to support @react-spring/web
    if (isAnimated) {
        return (
            // type for props should be reconsidered
            // React.CSSProperties?
            // @ts-ignore
            <LogoAnimation native={true}>
                {(animProps: any | undefined) => (
                    <animated.div style={{ ...animProps }}>{logoElement}</animated.div>
                )}
            </LogoAnimation>
        );
    }

    return logoElement;
}

export default Logo;
